export const PAGE_OUTLINE_TAG = "fishy-page-outline";

const HTMLElementBase = globalThis.HTMLElement ?? class {};
const HEADING_SELECTOR = "h1, h2, h3, h4, h5, h6";
const SECTION_HEADING_SELECTOR = "h2, h3, h4, h5, h6";
const MARKER_WIDTHS = ["w-4", "w-3", "w-2", "w-1.5", "w-1", "w-1"];
let nextOutlineId = 0;

function fragmentId(hash) {
  try {
    return decodeURIComponent(hash.slice(1));
  } catch {
    return hash.slice(1);
  }
}

function headingTarget(heading, source) {
  if (heading.id) return heading;
  const document = source.ownerDocument;
  for (const link of heading.querySelectorAll("a[href]")) {
    const url = new URL(link.href, document.baseURI);
    const page = document.location;
    if (!url.hash || url.origin !== page.origin || url.pathname !== page.pathname
        || url.search !== page.search) continue;
    const target = document.getElementById(fragmentId(url.hash));
    if (target && source.contains(target)
        && (target.contains(heading) || heading.contains(target))) return target;
  }
  // Zine puts fragment IDs on section wrappers, not necessarily on headings.
  // Do not turn an unanchored title into a link to the entire content root.
  for (let parent = heading.parentElement; parent && parent !== source; parent = parent.parentElement) {
    if (parent.matches("section[id], div[id]")
        && parent.querySelector(HEADING_SELECTOR) === heading) return parent;
  }
  return null;
}

function outlineMarkup(id) {
  return `
    <button type="button" class="btn btn-sm btn-ghost flex h-auto min-h-12 w-11 rounded-l-lg rounded-r-none border-0 px-2 py-2 text-base-content/45 shadow-none hover:bg-base-200/80 hover:text-base-content focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-primary"
            data-outline-trigger popovertarget="${id}" aria-controls="${id}"
            aria-expanded="false" aria-label="Open page outline" data-i18n-attr-aria-label="page_outline.open">
      <span data-outline-markers aria-hidden="true" class="flex max-h-[calc(100dvh-var(--fishy-page-outline-top)-2rem)] min-h-0 w-4 flex-col items-end"></span>
    </button>
    <nav id="${id}" popover="auto" data-outline-panel aria-labelledby="${id}-title"
         class="dropdown fixed inset-auto right-11 top-[calc(var(--site-nav-offset,0px)+1rem)] z-30 m-0 max-h-[calc(100dvh-var(--site-nav-offset,0px)-2rem)] w-[min(28rem,calc(100%-3.75rem))] overflow-y-auto overscroll-contain rounded-box border border-base-300 bg-base-100 p-2 text-base-content shadow-xl [position-area:none]">
      <div class="sticky top-0 z-10 flex items-center justify-between gap-3 bg-base-100 px-2 pb-2">
        <span id="${id}-title" class="text-xs font-semibold tracking-wide text-base-content/65" data-outline-title data-i18n-text="page_outline.title">On this page</span>
        <button type="button" class="btn btn-sm btn-square btn-ghost min-h-9 min-w-9" data-outline-close aria-label="Close page outline" data-i18n-attr-aria-label="page_outline.close">
          <svg class="fishy-icon size-4" viewBox="0 0 24 24" aria-hidden="true"><use width="100%" height="100%" href="#fishy-x-circle"></use></svg>
        </button>
      </div>
      <ul class="menu menu-sm w-full p-0" data-outline-links></ul>
    </nav>
  `;
}

export class FishyPageOutline extends HTMLElementBase {
  static observedAttributes = ["for"];

  constructor() {
    super();
    this._contentElement = null;
    this._explicitSource = false;
    this._entries = [];
    this._entryById = new Map();
    this._entryByLink = new Map();
    this._activeIndex = -1;
    this._frame = null;
    this._navigationFrame = null;
    this._hoverTimer = null;
    this._open = false;
    this._pinned = false;
    this._scrub = null;
    this._ignoreTriggerClick = false;
  }

  connectedCallback() {
    if (this._uiAbort) return;
    this._window = this.ownerDocument.defaultView;
    this._uiAbort = new this._window.AbortController();
    const { signal } = this._uiAbort;
    this.classList.add("fixed", "right-0", "top-[var(--fishy-page-outline-top)]", "z-30", "w-11", "not-prose", "print:hidden");
    if (!this._trigger) {
      this.innerHTML = outlineMarkup(`fishy-page-outline-${++nextOutlineId}`);
      this._trigger = this.querySelector("[data-outline-trigger]");
      this._panel = this.querySelector("[data-outline-panel]");
      this._links = this.querySelector("[data-outline-links]");
      this._markers = this.querySelector("[data-outline-markers]");
    }
    this.addEventListener("click", (event) => this._onClick(event), { signal });
    this.addEventListener("keydown", (event) => this._onKeyDown(event), { signal });
    this.addEventListener("pointerover", (event) => this._onPointerOver(event), { signal });
    this.addEventListener("pointerout", (event) => this._onPointerOut(event), { signal });
    this._trigger.addEventListener("pointerdown", (event) => this._onPointerDown(event), { signal });
    this._trigger.addEventListener("pointermove", (event) => this._onPointerMove(event), { signal });
    for (const type of ["pointerup", "pointercancel", "lostpointercapture"]) {
      this._trigger.addEventListener(type, (event) => {
        if (event.pointerId === this._scrub?.pointerId) this._endScrub();
      }, { signal });
    }
    this._window.addEventListener("blur", () => this._endScrub(), { signal });
    this.addEventListener("focusout", (event) => {
      if (!this._pinned && !this.contains(event.relatedTarget)) this._closePanel(false);
    }, { signal });
    this._panel.addEventListener("beforetoggle", (event) => {
      this._open = event.newState === "open";
      this._trigger.setAttribute("aria-expanded", String(this._open));
      if (!this._open) {
        this._pinned = false;
        this._endScrub();
      }
    }, { signal });
    this.ownerDocument.addEventListener("keydown", (event) => {
      if (event.key !== "Escape" || !this._open) return;
      event.preventDefault();
      this._closePanel(true);
    }, { capture: true, signal });
    const language = this._window.__fishystuffLanguage;
    this._localize();
    if (language?.event) this._window.addEventListener(language.event, () => this._localize(), { signal });
    language?.ready?.then(() => {
      if (!signal.aborted) this._localize();
    });
    this._window.addEventListener("pagehide", () => this._clearSource(), { signal });
    this._window.addEventListener("pageshow", (event) => {
      if (event.persisted) this._attachSource();
    }, { signal });
    this._attachSource();
  }

  disconnectedCallback() {
    this._clearSource();
    this._uiAbort?.abort();
    this._uiAbort = null;
  }

  attributeChangedCallback() {
    if (this._uiAbort && !this._explicitSource) this._attachSource();
  }

  get contentElement() {
    return this._contentElement;
  }

  set contentElement(value) {
    if (value !== null && !(value instanceof (this.ownerDocument.defaultView.HTMLElement))) {
      throw new TypeError("Page outline contentElement must be an HTMLElement or null.");
    }
    this._explicitSource = true;
    // Assignments are document transactions, including reuse of the same body node.
    this._contentElement = value;
    if (this._uiAbort) this._attachSource();
    else this._clearSource();
  }

  _localize() {
    this._window.__fishystuffLanguage?.apply(this);
  }

  _clearSource() {
    this._endScrub();
    this._closePanel(false);
    this._sourceAbort?.abort();
    this._sourceAbort = null;
    this._resizeObserver?.disconnect();
    this._resizeObserver = null;
    if (this._frame !== null) this._window.cancelAnimationFrame(this._frame);
    if (this._navigationFrame !== null) this._window.cancelAnimationFrame(this._navigationFrame);
    this._frame = null;
    this._navigationFrame = null;
    this._focusCleanup?.();
    this._entries = [];
    this._entryById.clear();
    this._entryByLink.clear();
    this._activeIndex = -1;
    this._navigationIndex = -1;
    this._links?.replaceChildren();
    this._markers?.replaceChildren();
    this.hidden = true;
  }

  _attachSource() {
    this._clearSource();
    if (!this._explicitSource) {
      this._contentElement = this.ownerDocument.getElementById(this.getAttribute("for") || "");
    }
    const source = this._contentElement;
    if (!source?.isConnected) return;
    const stack = [];
    for (const heading of source.querySelectorAll(SECTION_HEADING_SELECTOR)) {
      if (this.contains(heading) || heading.closest("[hidden], [inert], [aria-hidden='true']")) continue;
      const target = headingTarget(heading, source);
      const label = heading.textContent.trim();
      if (!target || !label || this._entryById.has(target.id)) continue;
      const level = Number(heading.localName[1]);
      while (stack.length && stack[stack.length - 1].level >= level) stack.pop();
      const parent = stack[stack.length - 1];
      const depth = stack.length;
      let list = this._links;
      if (parent) {
        if (!parent.children) {
          parent.children = this.ownerDocument.createElement("ul");
          parent.children.className = "ml-2 pl-3";
          parent.row.append(parent.children);
        }
        list = parent.children;
      }
      const row = this.ownerDocument.createElement("li");
      const link = this.ownerDocument.createElement("a");
      link.href = `#${encodeURIComponent(target.id)}`;
      link.textContent = label;
      link.className = "min-h-9 whitespace-normal break-words rounded-field px-2 py-2 text-sm leading-5 text-base-content/70 hover:text-base-content focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-primary aria-[current=location]:bg-base-200 aria-[current=location]:font-semibold aria-[current=location]:text-base-content";
      row.append(link);
      list.append(row);
      const marker = this.ownerDocument.createElement("span");
      marker.className = "flex h-3 min-h-0 w-full shrink items-center justify-end";
      const tick = this.ownerDocument.createElement("span");
      tick.className = "block h-0.5 max-h-full rounded-full bg-current opacity-45 data-active:opacity-100 data-active:scale-x-125 data-active:text-primary";
      tick.classList.add(MARKER_WIDTHS[Math.min(depth, MARKER_WIDTHS.length - 1)]);
      marker.append(tick);
      this._markers.append(marker);
      const entry = { heading, target, level, row, link, tick, top: 0, index: this._entries.length };
      this._entries.push(entry);
      this._entryById.set(target.id, entry);
      this._entryByLink.set(link, entry);
      stack.push(entry);
    }
    if (!this._entries.length) return;
    this.hidden = false;
    this._sourceAbort = new this._window.AbortController();
    const { signal } = this._sourceAbort;
    this._window.addEventListener("scroll", () => this._queueProjection(false), { passive: true, signal });
    this._window.addEventListener("resize", () => {
      this._endScrub();
      this._queueProjection(true);
    }, { passive: true, signal });
    this._window.addEventListener("hashchange", () => this._followHash(), { signal });
    source.addEventListener("toggle", () => this._queueProjection(true), { capture: true, signal });
    source.addEventListener("load", () => this._queueProjection(true), { capture: true, signal });
    this.ownerDocument.fonts?.addEventListener("loadingdone", () => this._queueProjection(true), { signal });
    this._resizeObserver = new this._window.ResizeObserver(() => this._queueProjection(true));
    this._resizeObserver.observe(source);
    // Images or banners before the article can move it without resizing it.
    if (this.ownerDocument.body !== source) this._resizeObserver.observe(this.ownerDocument.body);
    this._queueProjection(true);
    this._followHash();
  }

  _queueProjection(measure) {
    if (!this._sourceAbort || !this._entries.length) return;
    this._needsMeasure ||= measure;
    if (this._frame !== null) return;
    this._frame = this._window.requestAnimationFrame(() => {
      this._frame = null;
      if (this._needsMeasure) {
        this._needsMeasure = false;
        this._measurePositions();
      }
      this._projectActive();
    });
  }

  _measurePositions() {
    let previousTop = -Infinity;
    for (const entry of this._entries) {
      let visibleTarget = entry.heading;
      for (let parent = entry.heading.parentElement; parent && parent !== this._contentElement; parent = parent.parentElement) {
        if (parent.localName === "details" && !parent.open) visibleTarget = parent;
      }
      entry.top = Math.max(previousTop, visibleTarget.getBoundingClientRect().top + this._window.scrollY);
      previousTop = entry.top;
    }
    this._scrollPadding = parseFloat(this._window.getComputedStyle(this.ownerDocument.documentElement).scrollPaddingTop) || 0;
    this._scrollEnd = this.ownerDocument.documentElement.scrollHeight - this._window.innerHeight;
  }

  _projectActive() {
    const scrollTop = this._window.scrollY;
    if (scrollTop !== this._navigationScrollTop) this._navigationIndex = -1;
    const position = scrollTop + this._scrollPadding + 1;
    let low = 0;
    let high = this._entries.length;
    while (low < high) {
      const middle = (low + high) >>> 1;
      if (this._entries[middle].top <= position) low = middle + 1;
      else high = middle;
    }
    // Several final headings can share the same bottom-clamped scroll position.
    // Keep the explicitly selected heading until scrolling changes that position.
    const index = this._navigationIndex >= 0 ? this._navigationIndex
      : this._scrollEnd > 0 && scrollTop >= this._scrollEnd - 1
        ? this._entries.length - 1 : Math.max(0, low - 1);
    if (index === this._activeIndex) return;
    const previous = this._entries[this._activeIndex];
    previous?.link.removeAttribute("aria-current");
    previous?.tick.removeAttribute("data-active");
    this._activeIndex = index;
    const active = this._entries[index];
    active.link.setAttribute("aria-current", "location");
    active.tick.setAttribute("data-active", "");
  }

  _reveal(entry) {
    for (let parent = entry.heading.parentElement; parent && parent !== this._contentElement; parent = parent.parentElement) {
      if (parent.localName === "details" && !parent.open) {
        parent.open = true;
        this._queueProjection(true);
      }
    }
  }

  _focusHeading(heading) {
    this._focusCleanup?.();
    const addedTabIndex = !heading.hasAttribute("tabindex");
    if (addedTabIndex) heading.setAttribute("tabindex", "-1");
    const cleanup = () => {
      heading.removeEventListener("blur", cleanup);
      if (addedTabIndex && heading.getAttribute("tabindex") === "-1") heading.removeAttribute("tabindex");
      this._focusCleanup = null;
    };
    this._focusCleanup = cleanup;
    heading.addEventListener("blur", cleanup, { once: true });
    heading.focus({ preventScroll: true });
  }

  _navigate(entry, align, behavior = "auto") {
    // Global html scroll-padding owns the navbar offset.
    if (align) entry.target.scrollIntoView({ block: "start", behavior });
    this._focusHeading(entry.heading);
    this._navigationIndex = entry.index;
    this._navigationScrollTop = this._window.scrollY;
    this._queueProjection(false);
  }

  _queueNavigation(entry, align) {
    if (this._navigationFrame !== null) this._window.cancelAnimationFrame(this._navigationFrame);
    this._navigationFrame = this._window.requestAnimationFrame(() => {
      this._navigationFrame = null;
      // Ordinary links leave URL/history and the initial scroll to the browser.
      this._navigate(entry, align);
    });
  }

  _followHash() {
    const entry = this._entryById.get(fragmentId(this.ownerDocument.location.hash));
    if (!entry) return;
    this._reveal(entry);
    this._queueNavigation(entry, true);
  }

  _cancelHoverClose() {
    if (this._hoverTimer !== null) this._window.clearTimeout(this._hoverTimer);
    this._hoverTimer = null;
  }

  _openPanel(pin, focusIndex = null) {
    if (!this._entries.length) return;
    this._cancelHoverClose();
    this._pinned ||= pin;
    if (!this._open) this._panel.showPopover({ source: this._trigger });
    if (focusIndex !== null) this._entries[focusIndex]?.link.focus({ preventScroll: true });
  }

  _closePanel(restoreFocus) {
    this._endScrub();
    this._cancelHoverClose();
    if (!this._open) return;
    this._panel.hidePopover();
    if (restoreFocus && this.isConnected) this._trigger.focus({ preventScroll: true });
  }

  _onPointerOver(event) {
    if (event.pointerType !== "mouse" || event.buttons !== 0) return;
    this._cancelHoverClose();
    if (!this.contains(event.relatedTarget)) this._openPanel(false);
  }

  _onPointerOut(event) {
    if (event.pointerType !== "mouse" || this._pinned || this.contains(event.relatedTarget)) return;
    this._cancelHoverClose();
    this._hoverTimer = this._window.setTimeout(() => {
      this._hoverTimer = null;
      if (!this._pinned && !this.contains(this.ownerDocument.activeElement)) this._closePanel(false);
    }, 120);
  }

  _onPointerDown(event) {
    this._ignoreTriggerClick = false;
    if (event.pointerType !== "mouse" || event.button !== 0 || event.buttons !== 1
        || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey
        || event.defaultPrevented || !this._entries.length || !this._contentElement?.isConnected) return;
    const markers = this._markers.getBoundingClientRect();
    if (markers.height <= 0 || event.clientY < markers.top || event.clientY >= markers.bottom) return;
    const rail = this._trigger.getBoundingClientRect();
    this._endScrub();
    // Equal-height marker rows share the canonical entries' order. Measure once
    // per gesture; pointer moves neither scan headings nor read their layout.
    this._scrub = {
      pointerId: event.pointerId,
      top: markers.top,
      height: markers.height,
      left: rail.left,
      right: rail.right,
      index: -1,
    };
    this._trigger.setPointerCapture(event.pointerId);
    this._ignoreTriggerClick = true;
    event.preventDefault();
    if (this._navigationFrame !== null) this._window.cancelAnimationFrame(this._navigationFrame);
    this._navigationFrame = null;
    this._openPanel(true);
    this._scrubAt(event);
  }

  _onPointerMove(event) {
    if (event.pointerId !== this._scrub?.pointerId) return;
    if (event.buttons !== 1 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey
        || !this._contentElement?.isConnected) {
      this._endScrub();
      return;
    }
    event.preventDefault();
    this._scrubAt(event);
  }

  _scrubAt(event) {
    const scrub = this._scrub;
    if (event.clientX < scrub.left || event.clientX >= scrub.right
        || event.clientY < scrub.top || event.clientY >= scrub.top + scrub.height) return;
    const index = Math.floor((event.clientY - scrub.top) * this._entries.length / scrub.height);
    if (index === scrub.index) return;
    const entry = this._entries[index];
    const history = this._window.history;
    if (scrub.index === -1) history.pushState(history.state, "", entry.link.hash);
    else history.replaceState(history.state, "", entry.link.hash);
    scrub.index = index;
    this._reveal(entry);
    // Scrubbing settles synchronously: releasing/canceling capture cannot leave
    // a pending scroll or focus, and smooth scrolling cannot lag behind ticks.
    this._navigate(entry, true, "instant");
  }

  _endScrub() {
    const scrub = this._scrub;
    if (!scrub) return;
    this._scrub = null;
    if (this._trigger.hasPointerCapture(scrub.pointerId)) this._trigger.releasePointerCapture(scrub.pointerId);
  }

  _onClick(event) {
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.defaultPrevented) return;
    const trigger = event.target.closest("[data-outline-trigger]");
    if (trigger) {
      event.preventDefault();
      // Mouse capture retargets the release click to the rail, even after a drag
      // outside it. Only a fresh pointerdown or keyboard activation may toggle.
      if (event.detail !== 0 && this._ignoreTriggerClick) {
        this._ignoreTriggerClick = false;
        return;
      }
      if (this._open && this._pinned) this._closePanel(true);
      else this._openPanel(true, event.detail === 0 ? Math.max(0, this._activeIndex) : null);
      return;
    }
    if (event.target.closest("[data-outline-close]")) {
      this._closePanel(true);
      return;
    }
    const entry = this._entryByLink.get(event.target.closest("a"));
    if (!entry) return;
    this._reveal(entry);
    this._openPanel(true);
    this._queueNavigation(entry, false);
  }

  _onKeyDown(event) {
    if (event.target !== this._trigger || (event.key !== "ArrowDown" && event.key !== "ArrowUp")) return;
    event.preventDefault();
    this._openPanel(true, event.key === "ArrowUp" ? this._entries.length - 1 : 0);
  }
}

export function registerPageOutline() {
  if (typeof globalThis.customElements !== "undefined" && !globalThis.customElements.get(PAGE_OUTLINE_TAG)) {
    globalThis.customElements.define(PAGE_OUTLINE_TAG, FishyPageOutline);
  }
}

registerPageOutline();
